import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Field } from '@/components/ui/field';
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select';
import { SortableList } from '@/components/sortable-list';
import { slotFor } from '@/lib/slot-color';
import { CourseItemDraft } from '@/pages/courses/api';
import CourseItemSearch from './course-item-search';

// Assignment shortcut targets. Only builders that exist in this app today —
// the value is stored on the module and resolved to a deep link in the
// learner player.
const SHORTCUTS = [
  { value: '', label: 'None — written instructions only' },
  { value: 'planner', label: 'Planner — build a practice plan' },
];

export interface ModuleDraft {
  id: number;
  name: string;
  instructions: string;
  assignment_text: string;
  assignment_shortcut: string;
  items: CourseItemDraft[];
}

interface ModuleCardProps {
  module: ModuleDraft;
  index: number;
  // Field edits persist on blur; item changes persist immediately.
  onFieldsBlur: (module: ModuleDraft) => void;
  onFieldsChange: (module: ModuleDraft) => void;
  onItemsChange: (module: ModuleDraft, items: CourseItemDraft[]) => void;
  onRemove: (module: ModuleDraft) => void;
}

// One module in the builder's content step: header row (drag handle, number,
// remove), name + instructions, the item list with library search, and the
// optional assignment block. Mirrors the prototype's module card.
export default function ModuleCard({
  module,
  index,
  onFieldsBlur,
  onFieldsChange,
  onItemsChange,
  onRemove,
}: ModuleCardProps) {
  const [showSearch, setShowSearch] = useState(false);
  // Local UI toggle so opening the assignment editor doesn't have to write a
  // placeholder into the draft; a module with saved text opens expanded.
  const [assignmentOpen, setAssignmentOpen] = useState(module.assignment_text !== '');

  const patch = (changes: Partial<ModuleDraft>) => {
    onFieldsChange({ ...module, ...changes });
  };

  const divider = (label: string) => (
    <Flex align='center' gap={3}>
      <Box h='1px' bg='border' flex='1' />
      <Text
        fontFamily='heading'
        fontSize='2xs'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='0.16em'
        color='fg.muted'
      >
        {label}
      </Text>
      <Box h='1px' bg='border' flex='1' />
    </Flex>
  );

  return (
    <Box
      bg='bg.panel'
      borderWidth='1px'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      overflow='hidden'
    >
      {/* Header — the whole strip is the drag handle */}
      <Flex
        align='center'
        gap={2.5}
        px={5}
        py={3.5}
        borderBottomWidth='1px'
        borderColor='border'
      >
        <SortableList.DragHandle />
        <Center
          w='26px'
          h='26px'
          borderRadius='full'
          bg='brand.soft'
          fontFamily='heading'
          fontSize='xs'
          fontWeight='700'
          color='brand.fg'
          flexShrink={0}
        >
          {index + 1}
        </Center>
        <Text
          fontFamily='heading'
          fontSize='2xs'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.16em'
          color='fg.muted'
        >
          Module {index + 1}
        </Text>
        <Text ml='auto' fontSize='xs' color='fg.muted'>
          {module.items.length} {module.items.length === 1 ? 'item' : 'items'}
        </Text>
        <IconButton
          aria-label='Remove module'
          variant='ghost'
          size='xs'
          color='fg.subtle'
          _hover={{ color: 'danger' }}
          onClick={() => onRemove(module)}
        >
          <Icon as={XMarkIcon} w={4} h={4} />
        </IconButton>
      </Flex>

      <Stack gap={4} p={5}>
        <Field label='Module name'>
          <Input
            value={module.name}
            onChange={(e) => patch({ name: e.target.value })}
            onBlur={() => onFieldsBlur(module)}
            maxLength={200}
          />
        </Field>

        <Field
          label='How to complete this module'
          helperText='Shown to the learner above the content, before they mark it complete.'
        >
          <Textarea
            rows={3}
            value={module.instructions}
            onChange={(e) => patch({ instructions: e.target.value })}
            onBlur={() => onFieldsBlur(module)}
          />
        </Field>

        {divider('Add content')}

        {module.items.length > 0 && (
          <SortableList
            // Wrapped so the drag identity (uid) never collides with the
            // server row id the draft also carries.
            items={module.items.map((item) => ({ id: item.uid, draft: item }))}
            onChange={(reordered) =>
              onItemsChange(
                module,
                reordered.map((wrapped) => wrapped.draft),
              )
            }
            renderItem={({ id, draft }) => {
              const slot = slotFor(draft.content_type_name);
              return (
                <SortableList.Item id={id}>
                  <Flex
                    align='center'
                    gap={3}
                    px={3}
                    py={2.5}
                    borderWidth='1px'
                    borderColor='border'
                    borderRadius='md'
                    bg='bg.panel'
                    flex='1'
                  >
                    <SortableList.DragHandle />
                    <Box minW={0} flex='1'>
                      <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                        {draft.content_name}
                      </Text>
                      {draft.source_label && (
                        <Text fontSize='xs' color='fg.muted'>
                          {draft.source_label}
                        </Text>
                      )}
                    </Box>
                    {draft.content_type_name && (
                      <Badge
                        bg={`slot${slot}.bg`}
                        color={`slot${slot}.fg`}
                        fontSize='2xs'
                        textTransform='uppercase'
                      >
                        {draft.content_type_name}
                      </Badge>
                    )}
                    <IconButton
                      aria-label={`Remove ${draft.content_name}`}
                      variant='ghost'
                      size='xs'
                      color='fg.subtle'
                      _hover={{ color: 'danger' }}
                      onClick={() =>
                        onItemsChange(
                          module,
                          module.items.filter((existing) => existing.uid !== draft.uid),
                        )
                      }
                    >
                      <Icon as={XMarkIcon} w={3.5} h={3.5} />
                    </IconButton>
                  </Flex>
                </SortableList.Item>
              );
            }}
          />
        )}

        {showSearch ? (
          <CourseItemSearch
            onAdd={(item) => onItemsChange(module, [...module.items, item])}
            onDone={() => setShowSearch(false)}
          />
        ) : (
          <Button variant='outline' size='sm' onClick={() => setShowSearch(true)}>
            <Icon as={PlusIcon} w={4} h={4} color='brand.fg' />
            Add from your libraries
          </Button>
        )}

        {module.items.length === 0 && !showSearch && (
          <Center
            borderWidth='1px'
            borderStyle='dashed'
            borderColor='border.emphasized'
            borderRadius='md'
            py={5}
          >
            <Text fontSize='sm' color='fg.muted'>
              No content in this module yet — add some from your libraries.
            </Text>
          </Center>
        )}

        {divider('Assignment')}

        {assignmentOpen ? (
          <Box bg='bg.subtle' borderWidth='1px' borderColor='border' borderRadius='md' p={4}>
            <Flex align='center' justify='space-between' gap={3} mb={3}>
              <Text fontSize='sm' color='fg.muted'>
                The learner confirms this before the next module unlocks.
              </Text>
              <IconButton
                aria-label='Remove assignment'
                variant='ghost'
                size='xs'
                color='fg.subtle'
                _hover={{ color: 'danger' }}
                onClick={() => {
                  setAssignmentOpen(false);
                  const cleared = { ...module, assignment_text: '', assignment_shortcut: '' };
                  onFieldsChange(cleared);
                  onFieldsBlur(cleared);
                }}
              >
                <Icon as={XMarkIcon} w={4} h={4} />
              </IconButton>
            </Flex>
            <Stack gap={3}>
              <Field label='What the learner should do'>
                <Textarea
                  rows={2}
                  value={module.assignment_text}
                  onChange={(e) => patch({ assignment_text: e.target.value })}
                  onBlur={() => onFieldsBlur(module)}
                />
              </Field>
              <Field label='Shortcut' helperText='Drops the learner straight into that builder.'>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={module.assignment_shortcut}
                    onChange={(e) => {
                      const changed = { ...module, assignment_shortcut: e.currentTarget.value };
                      onFieldsChange(changed);
                      onFieldsBlur(changed);
                    }}
                  >
                    {SHORTCUTS.map((shortcut) => (
                      <option key={shortcut.value} value={shortcut.value}>
                        {shortcut.label}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Stack>
          </Box>
        ) : (
          <Button
            variant='ghost'
            size='sm'
            borderWidth='1px'
            borderStyle='dashed'
            borderColor='border.emphasized'
            color='fg.muted'
            onClick={() => setAssignmentOpen(true)}
          >
            <Icon as={PlusIcon} w={4} h={4} color='brand.fg' />
            Add an assignment the learner does before the next module
          </Button>
        )}
      </Stack>
    </Box>
  );
}
